import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Store.css';
import { badgesAPI, configAPI } from './services/api';

function Store({ currentUser }) {
  const navigate = useNavigate();
  const [currentBadgeCount, setCurrentBadgeCount] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [MAX_BADGES, setMaxBadges] = useState(10);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    loadBadgeCount();
    loadConfig();
  }, [currentUser, navigate]);

  const loadConfig = async () => {
    try {
      const config = await configAPI.getBadgeStoreConfig();
      setMaxBadges(config.maxBadges);
    } catch (error) {
      console.error('Error loading badge store config:', error);
    }
  };

  const loadBadgeCount = async () => {
    try {
      setLoading(true);
      const count = await badgesAPI.getGiveawayCount(currentUser.id);
      setCurrentBadgeCount(count);
    } catch (error) {
      console.error('Error loading badge count:', error);
      setMessage({ type: 'error', text: 'Failed to load badge count' });
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (purchasing) return;

    // Validate quantity
    if (quantity < 1) {
      setMessage({ type: 'error', text: 'Please select at least 1 badge' });
      return;
    }

    if (currentBadgeCount + quantity > MAX_BADGES) {
      setMessage({
        type: 'error',
        text: `Cannot purchase ${quantity} badges. You currently have ${currentBadgeCount} badges. Maximum allowed is ${MAX_BADGES}.`
      });
      return;
    }

    try {
      setPurchasing(true);
      setMessage({ type: '', text: '' });

      const response = await badgesAPI.purchaseBadges(quantity);

      setMessage({
        type: 'success',
        text: `You got ${quantity} free star${quantity > 1 ? 's' : ''}! Go spread some appreciation!`
      });

      // Update the badge count
      setCurrentBadgeCount(response.newBadgeCount);

      // Reset quantity
      setQuantity(1);
    } catch (error) {
      console.error('Error purchasing badges:', error);
      const errorMessage = error.response?.data?.message || error.response?.data || 'Failed to purchase badges';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setPurchasing(false);
    }
  };

  const getMaxPurchasableQuantity = () => {
    return Math.max(0, MAX_BADGES - currentBadgeCount);
  };

  const handleQuantityChange = (newQuantity) => {
    const maxAllowed = getMaxPurchasableQuantity();
    if (newQuantity < 1) {
      setQuantity(1);
    } else if (newQuantity > maxAllowed) {
      setQuantity(maxAllowed);
    } else {
      setQuantity(newQuantity);
    }
    // Clear error message when quantity changes
    if (message.type === 'error') {
      setMessage({ type: '', text: '' });
    }
  };

  if (loading) {
    return (
      <div className="store-container">
        <div className="store-loading">Loading...</div>
      </div>
    );
  }

  const maxPurchasable = getMaxPurchasableQuantity();
  const canPurchase = maxPurchasable > 0;

  return (
    <div className="store-container">
      <div className="store-content">
        <h1 className="store-title">Your Stars</h1>

        <div className="store-info-section">
          <div className="badge-count-display">
            <span className="badge-icon">⭐</span>
            <div className="badge-count-info">
              <div className="badge-count-label">Stars Ready to Give</div>
              <div className="badge-count-value">{currentBadgeCount} / {MAX_BADGES}</div>
            </div>
          </div>

          {!canPurchase && (
            <div className="info-message">
              You're holding the maximum of {MAX_BADGES} stars! Give some to people at gatherings and come back for more.
            </div>
          )}
        </div>

        {canPurchase && (
          <>
            <div className="store-product-section">
              <h2 className="section-title">Get Free Stars</h2>
              <p className="section-description">
                Stars are completely free! Collect them and give them to amazing people you meet at gatherings.
              </p>

              <div className="product-card">
                <div className="product-icon">⭐</div>
                <div className="product-details">
                  <div className="product-name">Star Badge</div>
                  <div className="product-free-label">Free</div>
                </div>
              </div>

              <div className="purchase-controls">
                <div className="quantity-selector">
                  <label htmlFor="quantity">How many stars?</label>
                  <div className="quantity-input-group">
                    <button
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <input
                      id="quantity"
                      type="number"
                      min="1"
                      max={maxPurchasable}
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                      className="quantity-input"
                    />
                    <button
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= maxPurchasable}
                    >
                      +
                    </button>
                  </div>
                  <div className="quantity-limit-text">
                    You can hold up to {maxPurchasable} more star{maxPurchasable !== 1 ? 's' : ''}
                  </div>
                </div>

                <button
                  className="buy-button"
                  onClick={handlePurchase}
                  disabled={purchasing || !canPurchase}
                >
                  {purchasing ? 'Getting your stars...' : `Get ${quantity} Star${quantity > 1 ? 's' : ''} — Free!`}
                </button>
              </div>
            </div>

            {message.text && (
              <div className={`store-message ${message.type}`}>
                {message.text}
              </div>
            )}

            <div className="store-info-box">
              <h3>How it works:</h3>
              <ul>
                <li>Stars are completely free to collect</li>
                <li>Give them to people you meet at gatherings as a thank-you</li>
                <li>You can hold up to {MAX_BADGES} stars at a time</li>
                <li>Add a personal message when you give a star to someone</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Store;
